<script>
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { portal } from 'svelte-portal';
  
  const dispatch = createEventDispatcher();
  
  // Core props
  export let position = { x: 0, y: 0 };
  export let query = '';
  export let items = [];
  export let selectedIndex = 0;
  export let activeSubmenu = null;
  export let selectedSubmenuIndex = 0;
  
  // DOM references
  let blockListElement;
  let submenuListElement;
  
  // Derived data
  $: filteredItems = (() => {
    // If no query, show normal hierarchical structure
    if (!query) {
      return items;
    }
    
    // With a query, flatten the structure to show matching submenu items directly
    const results = [];
    const lowerQuery = query.toLowerCase();
    
    items.forEach(item => {
      const itemString = `${item.title} ${item.description || ''}`.toLowerCase();
      const itemMatches = itemString.includes(lowerQuery);
      
      // Check if any submenu items match the search
      let hasMatchingSubmenuItems = false;
      let matchingSubmenuItems = [];
      if (item.hasSubmenu && item.submenuItems) {
        matchingSubmenuItems = item.submenuItems.filter(subItem => {
          const subItemString = `${subItem.title} ${subItem.description || ''}`.toLowerCase();
          return subItemString.includes(lowerQuery);
        });
        hasMatchingSubmenuItems = matchingSubmenuItems.length > 0;
      }
      
      // Show the item if either:
      // 1. The parent item matches, OR
      // 2. Any of its submenu items match
      if (itemMatches || hasMatchingSubmenuItems) {
        // Keep the item exactly as-is with its submenu intact
        results.push(item);
      }
      
      // ALSO add matching submenu items as standalone options
      // This allows direct selection when searching submenu content
      matchingSubmenuItems.forEach(subItem => {
        results.push({
          ...subItem,
          _isSubmenuItem: true,
          _parentTitle: item.title
        });
      });
    });
    
    return results;
  })();
  
  // Calculate submenu position
  let submenuX = 0;
  let submenuY = 0;
  let submenuPosition = 'right'; // 'right' or 'left'
  const MENU_WIDTH = 280;
  const SUBMENU_GAP = 0; // No gap - make menus adjacent
  
  $: if (position) {
    // Position submenu to the right of the main menu by default
    submenuX = position.x + MENU_WIDTH;
    submenuY = position.y;
    submenuPosition = 'right';
    
    // Check if submenu would go off screen horizontally
    if (window && (submenuX + MENU_WIDTH > window.innerWidth)) {
      // Position submenu to the left of the main menu
      submenuX = Math.max(0, position.x - MENU_WIDTH);
      submenuPosition = 'left';
    }
    
    // If menu is flipped (displayed above cursor), adjust submenu Y position
    // to align its bottom with the main menu's bottom
    if (position.flipVertical) {
      submenuY = position.y;
    }
  }
  
  // Ensure selected index stays in bounds
  $: {
    if (filteredItems.length > 0) {
      selectedIndex = Math.min(selectedIndex, filteredItems.length - 1);
    } else {
      selectedIndex = 0;
    }
  }
  
  // Scroll selected item into view when selectedIndex changes
  $: if (blockListElement && selectedIndex >= 0) {
    tick().then(() => {
      const selectedItem = blockListElement.querySelector('.block-item.selected');
      if (selectedItem) {
        // Get the container and item positions
        const containerRect = blockListElement.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();
        
        // Check if item is outside visible area
        if (itemRect.top < containerRect.top) {
          // Item is above visible area - scroll up
          selectedItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } else if (itemRect.bottom > containerRect.bottom) {
          // Item is below visible area - scroll down
          selectedItem.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }
    });
  }
  
  // Scroll selected submenu item into view
  $: if (submenuListElement && selectedSubmenuIndex >= 0 && activeSubmenu) {
    tick().then(() => {
      const selectedItem = submenuListElement.querySelector('.submenu-item.selected');
      if (selectedItem) {
        const containerRect = submenuListElement.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();
        
        if (itemRect.top < containerRect.top) {
          selectedItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } else if (itemRect.bottom > containerRect.bottom) {
          selectedItem.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }
    });
  }
  
  // Setup event handlers for clicks outside the menu
  function setupClickOutsideHandler() {
    const handleClickOutside = (event) => {
      // Check if click is outside both menus
      const mainMenu = document.querySelector('.block-menu');
      const submenu = document.querySelector('.submenu-container');
      
      if (mainMenu && !mainMenu.contains(event.target) && 
          (!submenu || !submenu.contains(event.target))) {
        dispatch('close');
      }
    };
    
    // For testing support, enable event detection immediately during render
    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    onMount(() => {
      // Additional handling for onMount (for live instance)
      if (typeof window !== 'undefined') {
        window.addEventListener('mousedown', handleClickOutside);
      }
      
      return () => {
        // Clean up both listeners
        if (typeof document !== 'undefined') {
          document.removeEventListener('mousedown', handleClickOutside);
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('mousedown', handleClickOutside);
        }
      };
    });
  }
  
  // Initialize the handler
  setupClickOutsideHandler();
  
  // Event handlers that dispatch to parent
  function handleBlockClick(block) {
    // If we're in search mode and this is a flattened submenu item, just select it
    if (query && block._isSubmenuItem) {
      dispatch('select', block);
    } else if (block.hasSubmenu) {
      // Allow submenu navigation even during search
      dispatch('activateSubmenu', block);
    } else {
      dispatch('select', block);
    }
  }
  
  function handleSubmenuClick(item) {
    dispatch('submenuSelect', item);
  }
  
  function handleBlockHover(index) {
    selectedIndex = index;
  }
</script>

<!-- Main menu -->
<div 
  class="block-menu"
  role="menubar"
  id="block-menu-container"
  aria-label="Block insertion menu"
  style="position: fixed; top: {position.y}px; left: {position.x}px; {position.flipVertical ? 'transform: translateY(-100%);' : ''}"
  class:flipped-menu={position.flipVertical}
  use:portal={'body'}
>
  <div class="search-query">
    {#if query}
      <span class="query-text">{query}</span><span class="blinking-cursor">|</span>
    {:else}
      <span class="blinking-cursor">|</span>
    {/if}
  </div>

  <div class="blocks-list" aria-labelledby="block-menu-container" aria-label="Available blocks" bind:this={blockListElement}>
    {#if filteredItems.length > 0}
      {#each filteredItems as item, i}
        <div class="block-wrapper">
          <button 
            class="block-item menu-item {i === selectedIndex ? 'active selected' : ''} {item._isSubmenuItem ? '_is-submenu-item' : ''}"
            role="menuitem"
            aria-haspopup={item.hasSubmenu ? 'menu' : undefined}
            aria-expanded={item.hasSubmenu && activeSubmenu === item ? 'true' : 'false'}
            on:mouseenter={() => handleBlockHover(i)}
            on:click={() => handleBlockClick(item)}
          >
            <span class="label">
              <strong>
                {#if item._isSubmenuItem}
                  {item.title}
                {:else}
                  {item.title}
                {/if}
              </strong>
              {#if item._isSubmenuItem}
                <small>from {item._parentTitle}</small>
              {:else if item.description}
                <small>{item.description}</small>
              {/if}
            </span>
            {#if item.hasSubmenu}
              <span class="submenu-arrow" aria-hidden="true">›</span>
            {/if}
          </button>
        </div>
      {/each}
    {:else}
      <div class="empty-results">
        <p>No results found for "{query}"</p>
      </div>
    {/if}
  </div>
</div>

<!-- Submenu if active -->
{#if activeSubmenu}
  <div 
    class="submenu-container submenu-{submenuPosition}"
    id="block-submenu-container"
    aria-label="{activeSubmenu.title} submenu"
    style="position: fixed; top: {submenuY}px; left: {submenuX}px; {position.flipVertical ? 'transform: translateY(-100%);' : ''}"
    class:flipped-menu={position.flipVertical}
    use:portal={'body'}
    bind:this={submenuListElement}
  >
    {#if activeSubmenu.submenuItems?.length > 0}
      {#each activeSubmenu.submenuItems as item, i}
        <button
          class="submenu-item menu-item {i === selectedSubmenuIndex ? 'active selected' : ''}"
          role="menuitem"
          on:mouseenter={() => selectedSubmenuIndex = i}
          on:click={() => handleSubmenuClick(item)}
        >
          <span class="label">
            <strong>{item.title}</strong>
            {#if item.description}
              <small>{item.description}</small>
            {/if}
          </span>
        </button>
      {/each}
    {:else}
      <button class="submenu-empty submenu-item">
        <span class="empty-message">There is currently no {activeSubmenu.title.toLowerCase()}!</span>
        <span class="empty-hint">Consider adding one via the toolbar</span>
      </button>
    {/if}
  </div>
{/if}

<style>
  .block-menu {
    z-index: var(--z-index-dropdown);
    width: 280px;
    padding: var(--spacing-2);
    margin: 0;
    overflow: visible;
    background: var(--color-spec-figure-bg);
    border: var(--border-width-1) solid var(--color-gray-400);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-dropdown);
  }
  
  /* Styles for when menu is flipped to display above cursor */
  .flipped-menu {
    box-shadow: var(--shadow-dropdown);
  }

  .search-query {
    padding: var(--spacing-2);
    font-size: 0.9em;
    color: var(--color-text-secondary);
  }

  .query-text {
    font-style: italic;
  }

  .blinking-cursor {
    font-weight: normal;
    color: var(--color-text-secondary);
    animation: blink 1s infinite;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  .blocks-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .block-wrapper {
    position: relative;
  }

  /* Shared styles for both menu items and submenu items */
  .block-item,
  .submenu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--spacing-3) var(--spacing-4);
    text-align: left;
    background: none;
    border: none;
    border-radius: var(--border-radius-md);
    transition: var(--transition-colors);
    cursor: pointer;
  }

  .block-item:hover,
  .block-item.active,
  .submenu-item:hover,
  .submenu-item.active {
    background: var(--color-bg-accent-30);
  }

  .block-item:focus,
  .submenu-item:focus {
    outline: 2px solid var(--color-spec-figure-text);
    outline-offset: -2px;
  }

  .block-item:focus:not(:focus-visible),
  .submenu-item:focus:not(:focus-visible) {
    outline: none;
  }

  /* Label styles shared between menu and submenu */
  .label {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0; /* Enable text truncation */
    gap: var(--spacing-1);
  }

  .label strong {
    overflow: hidden;
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    color: var(--color-text-primary);
    text-overflow: ellipsis;
  }

  .label small {
    overflow: hidden;
    font-size: 0.85em;
    white-space: nowrap;
    color: var(--color-text-secondary);
    text-overflow: ellipsis;
  }
  
  /* Style for flattened submenu items during search */
  .block-item._is-submenu-item .label {
    padding-left: var(--spacing-3);
  }
  
  .block-item._is-submenu-item .label small {
    font-style: italic;
    opacity: 0.8;
  }

  .submenu-arrow {
    margin-left: var(--spacing-2);
    font-size: 1.2em;
    color: var(--color-text-secondary);
    transition: var(--transition-transform);
    transform: rotate(0deg);
  }

  .block-item:hover .submenu-arrow {
    transform: rotate(90deg);
  }

  /* Submenu specific styles */
  .submenu-container {
    z-index: calc(var(--z-index-dropdown) + 1);
    width: 280px;
    max-height: 400px;
    padding: var(--spacing-2);
    margin: 0;
    overflow-y: auto;
    background: var(--color-spec-figure-bg);
    border: var(--border-width-1) solid var(--color-gray-400);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-dropdown);
  }
  
  /* Submenu positioning styles */
  .submenu-right {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  
  .submenu-left {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Scrollbar styles for both menu and submenu */
  .blocks-list::-webkit-scrollbar,
  .submenu-container::-webkit-scrollbar {
    width: var(--spacing-2);
  }

  .blocks-list::-webkit-scrollbar-track,
  .submenu-container::-webkit-scrollbar-track {
    background: var(--border-color-light);
    border-radius: var(--border-radius-md);
  }

  .blocks-list::-webkit-scrollbar-thumb,
  .submenu-container::-webkit-scrollbar-thumb {
    background: var(--border-color-default);
    border-radius: var(--border-radius-md);
  }

  .blocks-list::-webkit-scrollbar-thumb:hover,
  .submenu-container::-webkit-scrollbar-thumb:hover {
    background: var(--border-color-dark);
  }

  .submenu-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: var(--spacing-6);
    text-align: center;
    color: var(--color-text-secondary);
    background: none;
    border: none;
    cursor: default;
    gap: var(--spacing-2);
  }

  .submenu-empty:focus {
    background: none;
    outline: none;
  }

  .empty-message {
    font-size: 0.9em;
    color: var(--color-text-primary);
  }

  .empty-hint {
    font-size: 0.85em;
    color: var(--color-text-secondary);
    font-style: italic;
  }

  /* Empty results styles */
  .empty-results {
    padding: var(--spacing-4);
    text-align: center;
    color: var(--color-text-secondary);
    font-style: italic;
  }
</style>